import i18n from "i18next";
import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { dateColumn, enumColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import { PurchaseOrdersView } from "src/components/views/erp/purchase/purchaseOrders/PurchaseOrdersView.tsx";
import { OpenPurchaseOrderButton } from "src/components/views/erp/utilComponents/OpenPurchaseOrderButton.tsx";
import { PurchaseOrderCustomerOrdersApi } from "src/api/generated/erp/purchase/purchaseOrder/api/purchaseOrderCustomerOrdersApi.ts";
import { PurchaseOrderCustomerOrderView } from "src/api/generated/erp/db/types/tables/purchaseOrderCustomerOrderView.ts";
import { getPurchaseOrderCustomerOrderViewPurchaseOrderStateLabels } from "src/api/generated/erp/db/types/enums/purchaseOrderCustomerOrderViewPurchaseOrderState.ts";
import { faPlus, faUnlink } from "@fortawesome/pro-regular-svg-icons";
import { PurchaseOrderForm } from "src/components/views/erp/purchase/purchaseOrders/PurchaseOrderForm.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CustomerOrderLinkedPurchaseOrdersDataGridProps {
	customerOrderId: number;
}

export const CustomerOrderLinkedPurchaseOrdersDataGrid = ({
	customerOrderId,
}: CustomerOrderLinkedPurchaseOrdersDataGridProps) => {
	const showConfirmDialog = useConfirmDialog();
	const { openDialog } = useGenericDialog();

	return (
		<ClientSideDataGridModel<Array<PurchaseOrderCustomerOrderView>, PurchaseOrderCustomerOrderView, {}>
			gridId={"6B51F6E4F1C3B4B7"}
			fetchData={() => PurchaseOrderCustomerOrdersApi.getPurchaseOrdersForCustomerOrder({ customerOrderId })}
			getRows={(data) => data}
			getRowId={(row) => row.purchaseOrderCustomerOrderId}
			initialParams={{}}
			selectFirstRowOnLoad
			render={({ dataGridProps, onlySelectedRow, refreshData }) => (
				<AavoDataGrid<PurchaseOrderCustomerOrderView>
					columns={[
						integerColumn({
							field: "purchaseOrderId",
							headerName: i18n.t("number_shortened"),
						}),
						textColumn({
							field: "supplierName",
							headerName: i18n.t("supplier"),
							width: 180,
						}),
						enumColumn({
							field: "purchaseOrderState",
							headerName: i18n.t("state"),
							enumLabels: getPurchaseOrderCustomerOrderViewPurchaseOrderStateLabels(),
							width: 150,
						}),
						dateColumn({
							field: "plannedArrivalDate",
							headerName: i18n.t("planned_arrival_date"),
							width: 140,
						}),
						textColumn({
							field: "purchaseOrderReference",
							headerName: i18n.t("reference"),
							width: 200,
						}),
					]}
					actionBarComponents={
						<>
							<OpenPurchaseOrderButton
								purchaseOrderId={onlySelectedRow?.purchaseOrderId}
								variant={"outlined"}
							/>
							<AsyncButton
								label={i18n.t("create_purchase_order")}
								icon={faPlus}
								variant={"outlined"}
								onClick={() => {
									openDialog(({ closeDialog }) => ({
										title: i18n.t("purchase_order"),
										size: "xl",
										agentContext: {
											key: defineAgentUiContextKey("erp.purchase.purchaseOrder.customerOrderLinkedPurchaseOrdersDataGrid.formDialog"),
											data: {
												customerOrderId,
												purchaseOrderId: null,
											},
										},
										content: (
											<PurchaseOrderForm
												purchaseOrderId={undefined}
												createHandler={(request) =>
													PurchaseOrderCustomerOrdersApi.createPurchaseOrderForCustomerOrder({
														request,
														customerOrderId,
													})
												}
												onCompleted={async (result) => {
													await closeDialog();
													if (result.type === "success") {
														await refreshData();
													}
												}}
											/>
										),
									}));
								}}
							/>
							<AsyncButton
								label={i18n.t("unlink")}
								icon={faUnlink}
								variant={"outlined"}
								disabled={!onlySelectedRow}
								onClick={async () => {
									if (!onlySelectedRow) return;
									const confirmed = await showConfirmDialog();
									if (!confirmed) return;
									await PurchaseOrderCustomerOrdersApi.disconnectPurchaseOrderFromCustomerOrder({
										purchaseOrderCustomerOrderId: onlySelectedRow.purchaseOrderCustomerOrderId,
									});
									await refreshData();
								}}
							/>
						</>
					}
					onRowDoubleClick={(params) => {
						openDialog({
							title: i18n.t("purchase_order"),
							size: "xl",
							agentContext: {
								key: defineAgentUiContextKey("erp.purchase.purchaseOrder.customerOrderLinkedPurchaseOrdersDataGrid.dialog"),
								pathSegments: [`${i18n.t("purchase_order")} ${params.row.purchaseOrderId}`],
								data: {
									customerOrderId,
									purchaseOrderId: params.row.purchaseOrderId,
								},
							},
							content: <PurchaseOrdersView onlyPurchaseOrderId={params.row.purchaseOrderId} />,
						});
					}}
					agentContext={{
						key: defineAgentUiContextKey("erp.sales.customerOrderLinkedPurchaseOrders.grid"),
						data: {
							customerOrderId,
						},
						selectedRowPathSegment: (row) => `${i18n.t("purchase_order")} ${row.purchaseOrderId}`,
						rowFields: ["purchaseOrderId", "purchaseOrderCustomerOrderId", "supplierName"],
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};
